import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { listPricingConfigs } from "../../graphql/queries";
import { CreatePricingConfig, recalculateAllPricing } from "../../graphql/mutation";
import {
  Alert,
  Badge,
  Button,
  Card,
  Collapse,
  Form,
  Input,
  Modal,
  Spin,
  Table,
  Typography,
  message,
} from "antd";

const { Title, Text } = Typography;
const { Panel } = Collapse;

export const PricingConfigTab = () => {
  const { data, loading, error, refetch } = useQuery(listPricingConfigs);
  const [createPricingConfig] = useMutation(CreatePricingConfig);
  const [recalculateToolCosts, { loading: recalculating }] =
    useMutation(recalculateAllPricing);

  const [modalVisible, setModalVisible] = useState(false);
  const [saving, setSaving] = useState(false);

  // "Change" UX: inputs always represent the active/latest config (prefilled)
  const [formValues, setFormValues] = useState({
    proceedPerCredit: "",
    costCap: "",
    discount: "",
  });

  const configs = data?.listPricingConfigs || [];

  // Treat latest (most recent) as active. Assumes backend returns desc by createdAt.
  const latestConfig = useMemo(() => (configs.length ? configs[0] : null), [configs]);

  // Table data: active is first row
  const tableData = useMemo(
    () => configs.map((c, idx) => ({ ...c, key: c.id, isActive: idx === 0 })),
    [configs]
  );

  // Prefill form with latest config whenever it changes (and modal not open)
  useEffect(() => {
    if (!latestConfig) return;
    if (modalVisible) return;

    setFormValues({
      proceedPerCredit: String(latestConfig.proceedPerCredit ?? ""),
      costCap: String(latestConfig.costCap ?? ""),
      discount: String(latestConfig.discount ?? ""),
    });
  }, [latestConfig, modalVisible]);

  const openChangeModal = () => {
    if (latestConfig) {
      setFormValues({
        proceedPerCredit: String(latestConfig.proceedPerCredit ?? ""),
        costCap: String(latestConfig.costCap ?? ""),
        discount: String(latestConfig.discount ?? ""),
      });
    } else {
      setFormValues({ proceedPerCredit: "", costCap: "", discount: "" });
    }
    setModalVisible(true);
  };

  const parseNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : NaN;
  };

  const validateForm = () => {
    const proceedPerCredit = parseNumber(formValues.proceedPerCredit);
    const costCap = parseNumber(formValues.costCap);
    const discount = parseNumber(formValues.discount);

    if (!Number.isFinite(proceedPerCredit) || proceedPerCredit <= 0) {
      message.error("Proceed Per Credit must be a number > 0");
      return null;
    }
    if (!Number.isFinite(costCap) || costCap <= 0) {
      message.error("Cost Cap must be a number > 0");
      return null;
    }
    if (!Number.isFinite(discount) || discount < 0 || discount >= 1) {
      message.error("Discount must be between 0 and 1 (e.g. 0.1)");
      return null;
    }

    return { proceedPerCredit, costCap, discount };
  };

  const handleRecalculate = (configForPreview = latestConfig) => {
    if (!configForPreview) {
      message.warning("No pricing config found");
      return;
    }

    Modal.confirm({
      title: "Recalculate all credit costs?",
      content: (
        <div>
          <p>
            This will recalculate <strong>ALL</strong> credit costs using the active pricing
            configuration.
          </p>

          <div style={{ marginTop: 12 }}>
            <p style={{ marginBottom: 6 }}>
              <strong>Proceed Per Credit:</strong> {configForPreview.proceedPerCredit}
            </p>
            <p style={{ marginBottom: 6 }}>
              <strong>Cost Cap:</strong> {configForPreview.costCap}
            </p>
            <p style={{ marginBottom: 6 }}>
              <strong>Discount:</strong> {configForPreview.discount}
            </p>
          </div>

          <div style={{ marginTop: 14 }}>
            <Text type="warning">
              This updates stored credit costs across the system.
            </Text>
          </div>
        </div>
      ),
      okText: "Yes, Recalculate",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await recalculateToolCosts();
          message.success("Credit costs recalculated successfully");
        } catch (err) {
          message.error("Recalculation failed: " + (err?.message || "Unknown error"));
        }
      },
    });
  };

  const handleSaveChange = async () => {
    const parsed = validateForm();
    if (!parsed) return;

    setSaving(true);
    try {
      // Backend: create new config record (versioning), UI: "change active config"
      await createPricingConfig({
        variables: parsed,
      });

      message.success("Pricing updated (new version created).");
      setModalVisible(false);

      // Refresh list, then optionally prompt recalculation
      await refetch();

      // Encourage recalculation immediately after updating
      Modal.confirm({
        title: "Recalculate now?",
        content: (
          <div>
            <p>Pricing was updated. Do you want to recalculate credit costs now?</p>
            <Text type="secondary">
              If you skip, costs will remain based on the previous calculation until you run it.
            </Text>
          </div>
        ),
        okText: "Recalculate",
        okType: "primary",
        cancelText: "Not now",
        onOk: async () => {
          try {
            await recalculateToolCosts();
            message.success("Credit costs recalculated successfully");
          } catch (err) {
            message.error("Recalculation failed: " + (err?.message || "Unknown error"));
          }
        },
      });
    } catch (err) {
      message.error("Failed to update pricing: " + (err?.message || "Unknown error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 100 }}>
        <Spin size="large" />
        <p>Loading pricing configs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error loading pricing configs"
        description={error.message}
        type="error"
        showIcon
      />
    );
  }

  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 16,
          gap: 12,
        }}
      >
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Pricing Configuration
          </Title>
          <Text type="secondary">
            Only the latest configuration is active. Updates create a new version.
          </Text>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <Button type="primary" onClick={openChangeModal}>
            Change Pricing
          </Button>

          <Button loading={recalculating} onClick={() => handleRecalculate()}>
            Recalculate Credit Costs
          </Button>
        </div>
      </div>

      {/* Active Config Card */}
      <Card
        style={{
          marginBottom: 16,
          border: "1px solid #52c41a",
          background: "#f6ffed",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <Badge status="success" text="Active" />
          <Text strong>Current Pricing</Text>
          {latestConfig?.createdAt ? (
            <Text type="secondary" style={{ marginLeft: "auto" }}>
              Updated: {new Date(latestConfig.createdAt).toLocaleString()}
            </Text>
          ) : null}
        </div>

        {latestConfig ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div>
              <Text type="secondary">Proceed Per Credit</Text>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{latestConfig.proceedPerCredit}</div>
            </div>
            <div>
              <Text type="secondary">Cost Cap</Text>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{latestConfig.costCap}</div>
            </div>
            <div>
              <Text type="secondary">Discount</Text>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{latestConfig.discount}</div>
            </div>
          </div>
        ) : (
          <Alert
            type="warning"
            showIcon
            message="No pricing configuration found"
            description="Create your first pricing configuration using 'Change Pricing'."
          />
        )}
      </Card>

      {/* History (collapsed) */}
      <Collapse defaultActiveKey={[]} style={{ marginBottom: 24 }}>
        <Panel header="Pricing History" key="history">
          <Card>
            <Table
              dataSource={tableData}
              pagination={{ pageSize: 6 }}
              bordered
              rowClassName={(record) =>
                record.isActive ? "pricing-active-row" : "pricing-inactive-row"
              }
            >
              <Table.Column
                title="Proceed Per Credit"
                dataIndex="proceedPerCredit"
                key="proceedPerCredit"
              />
              <Table.Column title="Cost Cap" dataIndex="costCap" key="costCap" />
              <Table.Column title="Discount" dataIndex="discount" key="discount" />
              <Table.Column
                title="Created At"
                dataIndex="createdAt"
                key="createdAt"
                render={(text) => <span>{new Date(text).toLocaleString()}</span>}
              />
              <Table.Column
                title="Status"
                key="status"
                render={(_, record) =>
                  record.isActive ? (
                    <Badge status="success" text="Active" />
                  ) : (
                    <Badge status="default" text="Archived" />
                  )
                }
              />
            </Table>
          </Card>
        </Panel>
      </Collapse>

      <Modal
        title="Change Active Pricing Config"
        open={modalVisible}
        onOk={handleSaveChange}
        onCancel={() => setModalVisible(false)}
        confirmLoading={saving}
        okText="Save Change"
      >
        <Form layout="vertical">
          <Form.Item label="Proceed Per Credit  (e.g. 0.0123)" required>
            <Input
              type="number"
              value={formValues.proceedPerCredit}
              onChange={(e) =>
                setFormValues((prev) => ({ ...prev, proceedPerCredit: e.target.value }))
              }
              placeholder="e.g. 1.25"
            />
          </Form.Item>

          <Form.Item label="Cost Cap (e.g. 0.1)" required>
            <Input
              type="number"
              value={formValues.costCap}
              onChange={(e) => setFormValues((prev) => ({ ...prev, costCap: e.target.value }))}
              placeholder="e.g. 0.1"
            />
          </Form.Item>

          <Form.Item label="Discount  (e.g. 0.1)" required>
            <Input
              type="number"
              value={formValues.discount}
              onChange={(e) => setFormValues((prev) => ({ ...prev, discount: e.target.value }))}
              placeholder="e.g. 0.1"
            />
          </Form.Item>
        </Form>
      </Modal>

      <style>{`
        .pricing-active-row td { background: #e6fffb !important; }
        .pricing-inactive-row td { color: #aaa !important; opacity: 0.7; }
      `}</style>
    </div>
  );
};

export default PricingConfigTab;